import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FinancialBrokerDealer } from '../../../../../extViewmodels';
import { useErrorMessages, useTextMessaging, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getFormattedPhoneNumber } from '../../../../../models/UiUtils';
import { Button } from '../../../../components-v2/Button';
import { TextArea } from '../../../../components-v2/TextArea';
import { baseStyleSheet } from '../../../../styles/styles';
import { InputFieldError } from '../../../InputFieldError';
import { RadioButton } from '../../../RadioButton';
import { TextInput } from '../../../TextInput';
import { SettingsSubGroup } from '../../SettingsSubGroup';
import { styleSheet } from './styles';

interface IProps {
	onCancel?(): void;
}

export const TextingSettings: React.FC<IProps> = observer(({ onCancel }) => {
	const logger = useEventLogging();
	const errorMessages = useErrorMessages();
	const textMessagingVm = useTextMessaging();
	const userSession = useUserSession();
	const [forwardingEnabled, setForwardingEnabled] = useState(
		!!textMessagingVm?.phoneNumberOrder?.callForwardingNumber?.phoneNumber?.standard
	);

	// Check if the current user owns the number
	const currentUserOwnsNumber = textMessagingVm?.currentUserOwnsNumber;
	const [forwardingNumberErrorMessage, setForwardingNumberErrorMessage] = useState<string>('');
	const [forwardingPhoneNumber, setForwardingPhoneNumber] = useState('');
	const [userFirstTextMessageSignature, setUserFirstTextMessageSignature] = useState('');

	// This disclosure/signature was requested by Cetera so for now we are only showing it for accounts that have cetera.
	// IF the user has one already set we should also show it so they can remove it.
	const showTextDisclosure =
		userSession.account.additionalInfo.brokerDealer === FinancialBrokerDealer.Cetera ||
		userSession?.user?.userPreferences?.templatePreferences?.firstTextMessageSignature;

	const onCallForwardingComplete = async () => {
		const toasterMessage = 'Changes were saved successfully!';

		// Only handle call forwarding if the current user owns the number and it's not a hosted number
		if (currentUserOwnsNumber && !textMessagingVm?.phoneNumberOrder?.isHosted) {
			if (
				(!!textMessagingVm?.phoneNumberOrder?.id &&
					forwardingPhoneNumber !== undefined &&
					forwardingPhoneNumber !== '' &&
					forwardingNumberErrorMessage === '') ||
				!forwardingEnabled
			) {
				try {
					await textMessagingVm.onSetForwardingPhoneNumber(
						forwardingPhoneNumber,
						textMessagingVm?.phoneNumberOrder?.id
					);
				} catch (err) {
					logger.logApiError('Call Forwarding-Error', err);

					errorMessages.pushApiError(err);
				}
				textMessagingVm.getUserPhone();
			} else if (
				forwardingEnabled &&
				forwardingPhoneNumber === '' &&
				!!textMessagingVm?.phoneNumberOrder?.callForwardingNumber?.phoneNumber?.standard
			) {
				// DO NOTHING
			} else {
				setForwardingNumberErrorMessage('Please enter a valid phone number');
			}
		}

		try {
			await textMessagingVm.onSetTextSignatureDisclosure(userFirstTextMessageSignature);
		} catch (err) {
			logger.logApiError('Call Forwarding-Error', err);

			errorMessages.pushApiError(err);
		}
		toast.success(toasterMessage);
	};

	useEffect(() => {
		if (textMessagingVm?.phoneNumberOrder?.callForwardingNumber?.phoneNumber?.standard) {
			setForwardingEnabled(true);
		}
	}, [textMessagingVm]);

	// #region call forwarding callbacks
	const enableForwarding = () => {
		if (!forwardingEnabled) {
			setForwardingEnabled(true);
		}
	};

	const disableForwarding = () => {
		if (forwardingEnabled) {
			setForwardingEnabled(false);
			setForwardingPhoneNumber('');
			setForwardingNumberErrorMessage('');
		}
	};

	const onForwardingInputChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let value = (e.target.value || '').trim();
			try {
				value = getFormattedPhoneNumber(value);
				setForwardingPhoneNumber(value);
			} catch {
				setForwardingNumberErrorMessage('Please enter a valid phone number');
				setForwardingPhoneNumber('');
			}
			setForwardingPhoneNumber(value);
			setForwardingNumberErrorMessage('');
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[disableForwarding, enableForwarding]
	);

	const onUserFirstTextInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value || '';
		setUserFirstTextMessageSignature(value);
	};

	const onForwardingNumberInputBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
		let value = (e.target.value || '').trim();
		try {
			value = getFormattedPhoneNumber(value);
			setForwardingPhoneNumber(value);
		} catch {
			setForwardingNumberErrorMessage('Please enter a valid phone number');
			setForwardingPhoneNumber('');
		}
		setForwardingPhoneNumber(value);
		setForwardingNumberErrorMessage('');
	}, []);
	// #endregion

	const onCancelClick = () => {
		if (currentUserOwnsNumber) {
			setForwardingPhoneNumber('');
			setForwardingNumberErrorMessage('');
			setForwardingEnabled(!!textMessagingVm?.phoneNumberOrder?.callForwardingNumber?.phoneNumber?.standard);
		}
		// Close the accordion when Cancel is clicked
		onCancel?.();
	};

	const accountName = userSession.account.companyName;
	const newNumberMailto = `mailto:support@levitateapp.com?subject=${encodeURIComponent(
		accountName ? `New texting number request - ${accountName}` : 'New texting number request'
	)}`;
	const callForwardingMailto = `mailto:support@levitateapp.com?subject=${encodeURIComponent(
		accountName ? `Call forwarding inquiry - ${accountName}` : 'Call forwarding inquiry'
	)}`;

	const renderForwardingNumberInput = React.useCallback(() => {
		return (
			<div className={css(styleSheet.enableForwardingContainer)}>
				<span>Enable Call Forwarding:</span>
				<InputFieldError className={css(styleSheet.fieldErrorContainer)} errorMessage={forwardingNumberErrorMessage}>
					<TextInput
						className={css(styleSheet.callForwardingInput)}
						onChange={onForwardingInputChange}
						disabled={!forwardingEnabled}
						inputId='call-forwarding-phone-number'
						type='text'
						onBlur={onForwardingNumberInputBlur}
						placeholder={
							forwardingEnabled ? textMessagingVm?.phoneNumberOrder?.callForwardingNumber?.phoneNumber?.standard : ''
						}
						value={forwardingPhoneNumber}
					/>
				</InputFieldError>
			</div>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onForwardingInputChange, onCallForwardingComplete]);

	return (
		!!textMessagingVm?.phoneNumberOrder?.number && (
			<>
				<div className={css(styleSheet.sections)}>
					<SettingsSubGroup title='General'>
						<div>
							<span>
								Your Levitate texting number is{' '}
								{getFormattedPhoneNumber(textMessagingVm?.phoneNumberOrder?.number) || ' '}
							</span>
							<span className={css(styleSheet.contactSupport)}>
								<a className={css(styleSheet.supportLink)} href={newNumberMailto}>
									Contact support
								</a>{' '}
								to request a new number
							</span>
						</div>
					</SettingsSubGroup>
					{currentUserOwnsNumber && textMessagingVm?.phoneNumberOrder?.isHosted && (
						<SettingsSubGroup title='Call Forwarding'>
							<div className={css(styleSheet.hostedMessage)}>
								Call Forwarding is not available for this number.
								<br />
								This is a hosted number with texting capabilities only.
								<br />
								<a className={css(styleSheet.supportLink)} href={callForwardingMailto}>
									Contact support
								</a>{' '}
								for more information.
							</div>
						</SettingsSubGroup>
					)}
					{currentUserOwnsNumber && !textMessagingVm?.phoneNumberOrder?.isHosted && (
						<SettingsSubGroup title='Call Forwarding - Designate a number to dial if someone were to call the number above'>
							<div>
								<RadioButton
									className={css(styleSheet.marginBottom)}
									id='userTextingEnablementEnable'
									name='userTextingEnablement'
									onChange={enableForwarding}
									checked={forwardingEnabled}
								>
									{renderForwardingNumberInput()}
								</RadioButton>
								<RadioButton
									id='userTExtingEnablementDisable'
									name='userTextingEnablement'
									onChange={disableForwarding}
									checked={!forwardingEnabled}
								>
									Disable Call Forwarding
								</RadioButton>
							</div>
						</SettingsSubGroup>
					)}
					{showTextDisclosure ? (
						<SettingsSubGroup title='First Text Message Signature and Disclosure'>
							<div>
								<TextArea
									styles={[styleSheet.textSignatureDisclosureInput]}
									id='userFirstTextMessageSignature'
									onChange={onUserFirstTextInputChange}
									value={
										userFirstTextMessageSignature ||
										userSession?.user?.userPreferences?.templatePreferences?.firstTextMessageSignature ||
										''
									}
								/>
							</div>
						</SettingsSubGroup>
					) : null}
				</div>
				<div className={css(baseStyleSheet.mt5, baseStyleSheet.horizontalStack)}>
					<Button onClick={onCallForwardingComplete}>Save</Button>
					<Button onClick={onCancelClick} variant='secondary'>
						Cancel
					</Button>
				</div>
			</>
		)
	);
});
